import * as React from 'react';
import { useEffect, useState } from 'react';
import { useQueryData } from 'hook/useQueryData';
import { useFindBySpecificationQuery } from 'services/entityApi';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slide,
  Stack,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { selectEntity, unselectEntity } from 'slice/searchSlice';
import MDTypography from 'components/MDTypography';
import CloseIcon from '@mui/icons-material/Close';
import MDButton from 'components/MDButton';
import useConf from 'hook/useConf';
import { useGetConfigurationQuery } from 'services/applicationApi';
import { useIntl } from 'react-intl';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import LinearProgress from '@mui/material/LinearProgress';
import Box from '@mui/material/Box';
import EntityDetailsPanel from 'pages/entities/details/EntityDetailsPanel';

ContainerWithEntityDetails.propTypes = {
  view: PropTypes.string.isRequired,
  selectedStateSelector: PropTypes.func.isRequired,
  resultsStateSelector: PropTypes.func,
  children: PropTypes.node.isRequired
};

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

export default function ContainerWithEntityDetails({
  view,
  selectedStateSelector,
  resultsStateSelector,
  children
}) {
  const { formatMessage: msg } = useIntl();
  const { getValue: conf } = useConf(useGetConfigurationQuery);
  const detailedView = conf(`${view}.result.detailedView`);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const selectedId = useSelector(selectedStateSelector);
  const searchResults = useSelector(resultsStateSelector);
  const dispatch = useDispatch();

  const [entity, setEntity] = useState(null);
  const [index, setIndex] = useState(-1);

  const { data, loading } = useQueryData(
    useFindBySpecificationQuery,
    {
      viewName: detailedView,
      filter: `id = ${selectedId || -1}`,
      size: 1,
      jsonView: 'WithParent'
    },
    { skip: !detailedView || !conf(`${detailedView}.enabled`, false) }
  );

  useEffect(() => {
    if (!loading && data && data.content?.length > 0) {
      setEntity(data.decode().content[0]);
      setIndex(searchResults.findIndex((element) => element.entityId === selectedId));
    }
  }, [data, loading, searchResults, selectedId]);

  const handleDialogClose = () => {
    setEntity(null);
    setIndex(-1);
    dispatch(unselectEntity());
  };

  return (
    <>
      {children}
      {(!loading || index >= 0) && entity && (
        <Dialog
          fullScreen={fullScreen}
          fullWidth
          maxWidth="xl"
          open={selectedId >= 0}
          onClose={handleDialogClose}
          TransitionComponent={Transition}
        >
          <DialogTitle sx={{ m: 0, p: 2, mr: 5 }}>
            <MDTypography>{entity.name}</MDTypography>
            <IconButton
              aria-label={msg({ id: 'action.close' })}
              onClick={handleDialogClose}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500]
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {loading && (
              <Box sx={{ width: '100%' }}>
                <LinearProgress />
              </Box>
            )}
            <EntityDetailsPanel view={detailedView} entity={entity} />
          </DialogContent>
          <DialogActions>
            <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
              <Stack direction="row" spacing={1}>
                {searchResults && searchResults.length > 0 && (
                  <>
                    <IconButton
                      disabled={index <= 0}
                      color="primary"
                      aria-label={msg({ id: 'action.previous' })}
                      onClick={() => {
                        dispatch(selectEntity(searchResults[index - 1].entityId));
                      }}
                    >
                      <KeyboardDoubleArrowLeftIcon fontSize="large" />
                    </IconButton>
                    <IconButton
                      disabled={index >= searchResults.length - 1}
                      color="primary"
                      aria-label={msg({ id: 'action.next' })}
                      onClick={() => {
                        dispatch(selectEntity(searchResults[index + 1].entityId));
                      }}
                    >
                      <KeyboardDoubleArrowRightIcon fontSize="large" />
                    </IconButton>
                  </>
                )}
              </Stack>

              <MDButton autoFocus onClick={handleDialogClose}>
                {msg({ id: 'action.close' })}
              </MDButton>
            </Stack>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
